<script setup lang="ts">
import {
  IonButton,
  IonPage,
  IonToast,
  onIonViewDidEnter,
  onIonViewWillLeave,
} from '@ionic/vue'
import { nextTick, provide, ref, watch } from 'vue'
import { SwiperSlide } from 'swiper/vue'
import { storeToRefs } from 'pinia'
import SekaiMainPage from './SekaiMainPage.vue'
import DebugModal from '@/components/DebugModal.vue'
import PageHeader from '@/components/PageHeader.vue'
import TapTipToast from '@/components/TapTipToast.vue'
import LoadingComponent from '@/components/LoadingComponent.vue'
import { useFetch } from '@/composables/useFetch'
import { useBgm } from '@/composables/useBgm'
import { usePreferencesStore } from '@/store/preferences'
import { useUserStore } from '@/store/user'
import { useChatsStore } from '@/store/chats'
import { homePageViewEnteredKey } from '@/constant/providerKey'
import type { ReturnedUniverse } from '@/services/universe'
import { recommendationService } from '@/services/recommendation'
import { generateChatFromReturnedUniverse } from '@/utils/generateChatFromUniverse'
import type { Chat } from '@/types'
import 'swiper/css'
import { useGlobalStateStore } from '@/store/globalState'
import NameChangeModal from '@/components/NameChangeModal.vue'

const preferencesStore = usePreferencesStore()
const userStore = useUserStore()

const toastIsOpen = ref(false)
const tipIsOpen = ref(false)
const searchUniverseModalIsOpen = ref(false)

const chatStore = useChatsStore()
const globalStateStore = useGlobalStateStore()
const { disableSwipe, openNameChange } = storeToRefs(globalStateStore)
const { chats } = chatStore
const hasInit = ref(true)
const { fetchData, data, error, loading } = useFetch<ReturnedUniverse[]>()
const bgm = useBgm()

watch(
  () => data.value,
  (newData) => {
    if (newData) {
      const newChats: Chat[] = []
      for (let i = 0; i < newData.length; i++) {
        const universe = newData[i]
        const chat = generateChatFromReturnedUniverse(universe)
        newChats.push(chat)
      }

      const prevCount = chatStore.chats.length
      chatStore.addChats(newChats)

      if (prevCount === 0) {
        // play the first chat bgm
        bgm.playBgm(newChats[chatStore.currentChatIndex].bgmUrl)
        chatStore.currentChatId = newChats[chatStore.currentChatIndex].chatId
      }
      // TODO: Find a better way to handle the init
      // first time init will not enable the swipe cursor
      nextTick(() => {
        // refresh page
        hasInit.value = false
        nextTick(() => {
          hasInit.value = true
        })
      })
    }
  },
)

async function fetchSekai() {
  return await recommendationService.getUniverses({
    favoriteCharGroupIds: [],
    favoriteCharIds: [],
    favoriteTagIds: [],
  })
}

function reportWatchSekai(index) {
  const chat = chats[index]
  chatStore.reportWatchedUniverse(chat)
}

const isViewEntered = ref(false)
provide(homePageViewEnteredKey, isViewEntered)

onIonViewDidEnter(async () => {
  isViewEntered.value = true
  if (searchUniverseModalIsOpen.value === false) {
    await fetchData(fetchSekai)
  }
  bgm.setPageActive(true)

  const currentBgmUrl = chats?.[chatStore.currentChatIndex]?.bgmUrl
  if (!currentBgmUrl) {
    return
  }
  // if current bgm url is equal to the current index chat bgm url
  // then play the bgm
  if (bgm.bgmUrl.value === currentBgmUrl) {
    bgm.resumeBgm()
  }
  else {
    if (bgm.bgmUrl.value !== '') {
      bgm.stopBgm()
    }
    bgm.playBgm(currentBgmUrl)
  }
})

onIonViewWillLeave(() => {
  isViewEntered.value = false
  bgm.stopBgm()
  bgm.setPageActive(false)
})

watch(
  () => userStore.isLoggedIn,
  (newVal) => {
    if (!newVal) {
      toastIsOpen.value = true
    }
  },
)

watch(
  () => chatStore.currentChatIndex,
  (newVal) => {
    if (newVal >= chats.length - 2) {
      fetchData(fetchSekai)
    }
  },
)

watch(
  () => preferencesStore.preferencesAreLoaded,
  (newVal) => {
    if (newVal) {
      if (preferencesStore.firstTimeOpen) {
        showOnboardAnimation()
      }
    }
  },
)

function showOnboardAnimation() {
  tipIsOpen.value = true
  setTimeout(() => {
    tipIsOpen.value = false
    preferencesStore.firstTimeOpen = false
  }, 4500)
}
</script>

<template>
  <ion-page class="bg-#333">
    <debug-modal />
    <ion-toast
      :is-open="toastIsOpen"
      :message="$t('homePage.reLogin')"
      :duration="3000"
      position="middle"
      class="custom-toast"
      @did-dismiss="() => (toastIsOpen = false)"
    />
    <tap-tip-toast v-if="tipIsOpen" />
    <loading-component v-if="(loading || error) || data?.length === 0">
      <ion-button
        v-if="error"
        fill="clear"
        size="small"
        @click="
          async () => {
            await userStore.logOut();
            await fetchData(fetchSekai);
          }
        "
      >
        <div
          class="px-3.5 py-1.5 rounded-full bg-[#00f5ff] text-black text-3.5 fw-600 leading-4.5"
        >
          <p>Network Error, Please retry</p>
        </div>
      </ion-button>
    </loading-component>
    <swiper-container
      v-else
      class="sekai-swiper"
      :init="hasInit"
      :allow-slide-next="!disableSwipe"
      :grab-cursor="true"
      :slides-per-view="1"
      :lazy-loading="true"
      :touch-start-prevent-default="false"
      direction="vertical"
      @swiperslidechange="
        (event) => {
          const [swiper] = event.detail;
          const index = swiper.activeIndex;
          chatStore.setCurrentChatIndex(index);
          chatStore.setCurrentChatId(chats[index].chatId);
          bgm.playBgm(chats[index].bgmUrl);
          reportWatchSekai(index);
        }
      "
    >
      <swiper-slide v-for="(chat, index) in chats" :key="chat.chatId">
        <sekai-main-page
          :key="chat.chatId"
          :chat="chat"
          :next-chat="chats[index + 1]"
          :index="index"
          :is-preview="false"
          :is-loading="false"
          :active="isViewEntered"
          @play-bgm="() => bgm.playBgmByTap()"
          @stop-bgm="
            () => {
              bgm.stopBgm(true);
            }
          "
        />
      </swiper-slide>
    </swiper-container>
    <page-header
      :is-single-sekai="false"
    />
    <name-change-modal
      :open="openNameChange"
      :can-dismiss="true"
      @close="() => {
        globalStateStore.setOpenNameChange(false)
      }"
    />
  </ion-page>
</template>

<style scoped>
ion-toast.custom-toast {
  --background: var(--ion-background-color);
  --spinner-color: var(--ion-text-secondary-color);
  color: var(--ion-text-secondary-color);
}

.sekai-swiper {
  width: 100dvw;
  height: 100dvh;
  position: fixed;
  top: 0;
  left: 0;
}
.swiper-slide {
  width: 100%;
  background-position: center;
  background-size: cover;
}
</style>
